'use client';

import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faHatWizard } from '@fortawesome/pro-solid-svg-icons';
import { EmptyState } from '../EmptyState';
import {
  DataTableHeader,
  DataTableCell,
  DataTablePagination,
} from './components';

interface ToggleOption {
  code: string;
  label: string;
}

interface Column {
  label: string;
  column: string;
  type: 'static' | 'dynamic';
  toggle?: ToggleOption[];
  sortable?: boolean;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
}

interface DataTableData {
  count: number;
  next: string | null;
  previous: string | null;
  results: any[];
}

interface SortValues {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface DataTableProps {
  columns: Column[];
  data: DataTableData;
  isLoading?: boolean;
  sortValues?: SortValues;
  onSortValuesChange?: (sortValues: SortValues) => void;
  id?: string;
  'aria-label'?: string;
  selectable?: boolean;
  onRowSelect?: (row: any, isSelected: boolean) => void;
  selectedRows?: string[];
  rowIdField?: string | ((row: any) => string);
  emptyState?: {
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    className?: string;
  };
  loadingState?: {
    overlay?: boolean;
    message?: string;
    className?: string;
  };
  styling?: {
    className?: string;
    headerClassName?: string;
    rowClassName?: string;
    cellClassName?: string;
    maxHeight?: string | number;
    minColumnWidth?: string | number;
  };
  renderCell?: (
    value: any,
    row: any,
    column: Column,
    rowIndex: number,
    columnIndex: number
  ) => React.ReactNode;
  renderHeader?: (column: Column, index: number) => React.ReactNode;
  onRowClick?: (row: any, rowIndex: number) => void;
  onCellClick?: (
    value: any,
    row: any,
    column: Column,
    rowIndex: number,
    columnIndex: number
  ) => void;
  showPagination?: boolean;
  PaginationComponent?: React.ComponentType<{
    count: number;
    sortValues: SortValues;
    setSortValues: (values: SortValues) => void;
  }>;
  onSort?: (column: Column, direction: 'asc' | 'desc' | null) => void;
  onToggleChange?: (column: Column, toggleIndex: number) => void;
  pinnedColumns?: { left?: string[]; right?: string[] };
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data = { count: 0, next: null, previous: null, results: [] },
  isLoading = false,
  sortValues = { page: 1, pageSize: 50 },
  onSortValuesChange,
  id = 'data-table',
  'aria-label': ariaLabel = 'data-table',
  selectable = false,
  onRowSelect,
  selectedRows = [],
  rowIdField = 'id',
  emptyState,
  loadingState = {},
  styling = {},
  renderCell,
  renderHeader,
  onRowClick,
  onCellClick,
  showPagination = true,
  PaginationComponent = DataTablePagination,
  onSort,
  onToggleChange,
  pinnedColumns = { left: [], right: [] },
}) => {
  const [toggleStates, setToggleStates] = useState<Record<number, number>>({});

  const getColumnPinnedStatus = useCallback(
    (columnName: string): 'left' | 'right' | false => {
      if (pinnedColumns.left?.includes(columnName)) return 'left';
      if (pinnedColumns.right?.includes(columnName)) return 'right';
      return false;
    },
    [pinnedColumns]
  );

  const { leftPinnedColumns, rightPinnedColumns, unpinnedColumns } =
    useMemo(() => {
      const leftPinned = columns.filter((col) =>
        pinnedColumns.left?.includes(col.column)
      );
      const rightPinned = columns.filter((col) =>
        pinnedColumns.right?.includes(col.column)
      );
      const unpinned = columns.filter(
        (col) =>
          !pinnedColumns.left?.includes(col.column) &&
          !pinnedColumns.right?.includes(col.column)
      );

      return {
        leftPinnedColumns: leftPinned,
        rightPinnedColumns: rightPinned,
        unpinnedColumns: unpinned,
      };
    }, [columns, pinnedColumns]);

  const leftPinnedGrid = useMemo(() => {
    return leftPinnedColumns
      .map((column) => {
        if (column.width) {
          return typeof column.width === 'number'
            ? `${column.width}px`
            : column.width;
        }
        return 'minmax(120px, 1fr)';
      })
      .join(' ');
  }, [leftPinnedColumns]);

  const rightPinnedGrid = useMemo(() => {
    return rightPinnedColumns
      .map((column) => {
        if (column.width) {
          return typeof column.width === 'number'
            ? `${column.width}px`
            : column.width;
        }
        return 'minmax(120px, 1fr)';
      })
      .join(' ');
  }, [rightPinnedColumns]);

  const unpinnedGrid = useMemo(() => {
    if (styling.minColumnWidth) {
      return `repeat(${unpinnedColumns.length}, minmax(${styling.minColumnWidth}, 1fr))`;
    }

    return unpinnedColumns
      .map((column) => {
        if (column.width) {
          return typeof column.width === 'number'
            ? `${column.width}px`
            : column.width;
        }
        return 'minmax(120px, 1fr)';
      })
      .join(' ');
  }, [unpinnedColumns, styling.minColumnWidth]);

  const getRowId = (row: any): string => {
    if (typeof rowIdField === 'function') {
      return rowIdField(row);
    }

    if (typeof rowIdField === 'string') {
      return row[rowIdField] || row.id || row.provider_id || row.providerId;
    }

    return row.id || row.provider_id || row.providerId;
  };

  const handleSortValues = (newSortValues: SortValues) => {
    onSortValuesChange?.(newSortValues);
  };

  const handleRowClick = (row: any, rowIndex: number) => {
    if (selectable && onRowSelect) {
      const rowId = getRowId(row);
      const isSelected = selectedRows.includes(rowId);
      onRowSelect(row, !isSelected);
    } else if (onRowClick) {
      onRowClick(row, rowIndex);
    }
  };

  const handleCellClick = (
    value: any,
    row: any,
    column: Column,
    rowIndex: number,
    columnIndex: number
  ) => {
    onCellClick?.(value, row, column, rowIndex, columnIndex);
  };

  const handleSort = (column: Column, direction: 'asc' | 'desc' | null) => {
    if (onSort) {
      onSort(column, direction);
    } else if (onSortValuesChange) {
      onSortValuesChange({
        ...sortValues,
        sortBy: column.column,
        sortOrder: direction,
      });
    }
  };

  const handleToggleChange = (column: Column, toggleIndex: number) => {
    if (onToggleChange) {
      onToggleChange(column, toggleIndex);
    } else {
      const dynamicColumnIndex = columns
        .slice(0, columns.indexOf(column))
        .filter((c) => c.type === 'dynamic').length;

      setToggleStates((prev) => ({
        ...prev,
        [dynamicColumnIndex]: toggleIndex,
      }));
    }
  };

  const renderColumnSection = (sectionColumns: Column[], startIndex = 0) => {
    if (sectionColumns.length === 0) return null;

    return sectionColumns.map((column, sectionIndex) => {
      const globalIndex = startIndex + sectionIndex;

      const dynamicColumnIndex = columns
        .slice(0, globalIndex)
        .filter((c) => c.type === 'dynamic').length;
      const currentToggleIndex =
        column.type === 'dynamic' ? toggleStates[dynamicColumnIndex] || 0 : 0;

      if (renderHeader) {
        return (
          <React.Fragment key={`header-${column.column}-${globalIndex}`}>
            {renderHeader(column, globalIndex)}
          </React.Fragment>
        );
      }

      return (
        <DataTableHeader
          key={`header-${column.label}-${globalIndex}`}
          column={column}
          isLastColumn={false}
          currentToggleIndex={currentToggleIndex}
          onToggleChange={(toggleIndex) =>
            handleToggleChange(column, toggleIndex)
          }
          index={globalIndex}
          sortDirection={
            sortValues.sortBy === column.column
              ? sortValues.sortOrder || null
              : null
          }
          onSort={() => {
            const currentDirection =
              sortValues.sortBy === column.column ? sortValues.sortOrder : null;
            const newDirection =
              currentDirection === 'asc'
                ? 'desc'
                : currentDirection === 'desc'
                ? 'asc'
                : 'asc';
            handleSort(column, newDirection);
          }}
          className={styling.headerClassName}
          pinned={getColumnPinnedStatus(column.column)}
        />
      );
    });
  };

  const renderDataSection = (
    sectionColumns: Column[],
    row: any,
    rowIndex: number,
    startIndex = 0
  ) => {
    if (sectionColumns.length === 0) return null;

    return sectionColumns.map((column, sectionIndex) => {
      const globalIndex = startIndex + sectionIndex;

      const dynamicColumnIndex = columns
        .slice(0, globalIndex)
        .filter((c) => c.type === 'dynamic').length;
      const currentToggleIndex =
        column.type === 'dynamic' ? toggleStates[dynamicColumnIndex] || 0 : 0;

      let cellValue = '';
      if (column.type === 'static') {
        cellValue = row[column.column] || '';
      } else if (column.type === 'dynamic') {
        const currentToggle = column.toggle?.[currentToggleIndex];
        const dataKey = currentToggle?.code;
        cellValue = row[column.column]?.[dataKey] ?? '';
      }

      const renderedCell = renderCell
        ? renderCell(cellValue, row, column, rowIndex, globalIndex)
        : null;

      return (
        <DataTableCell
          key={`${rowIndex}-${globalIndex}`}
          column={column}
          row={row}
          isLastColumn={false}
          currentToggleIndex={currentToggleIndex}
          rowIndex={rowIndex}
          columnIndex={globalIndex}
          value={cellValue}
          renderedCell={renderedCell}
          onClick={() =>
            handleCellClick(cellValue, row, column, rowIndex, globalIndex)
          }
          className={styling.cellClassName}
          pinned={getColumnPinnedStatus(column.column)}
        />
      );
    });
  };

  return (
    <div
      className={`w-full relative ${styling.className || ''}`}
      role="region"
      aria-label={ariaLabel}
    >
      {!isLoading && (!data || !data.results || data.results.length === 0) ? (
        <EmptyState
          icon={emptyState?.icon || <Icon icon={faHatWizard} size="2xl" />}
          title={emptyState?.title || 'No items available'}
          description={
            emptyState?.description ||
            'There are no items available for that list'
          }
          className={
            emptyState?.className || 'h-96 flex items-center justify-center'
          }
        />
      ) : (
        <>
          <div
            className={`relative overflow-auto bg-white ${
              styling.maxHeight ? `max-h-${styling.maxHeight}` : 'max-h-144'
            }`}
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}
            role="table"
            aria-label={ariaLabel}
            aria-rowcount={data?.results?.length || 0}
            aria-colcount={columns.length}
            id={id}
          >
            {leftPinnedColumns.length > 0 && (
              <div
                className="flex-shrink-0 sticky left-0 z-20 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.1)]"
                style={{
                  display: 'grid',
                  gridTemplateColumns: leftPinnedGrid,
                }}
                role="rowgroup"
                aria-label="Left pinned columns"
              >
                <div className="contents" role="row">
                  {renderColumnSection(leftPinnedColumns, 0)}
                </div>

                {data?.results?.map((row, rowIndex) => {
                  const rowId = getRowId(row);
                  const isSelected = selectedRows.includes(rowId);

                  return (
                    <div
                      key={`left-pinned-row-${rowIndex}`}
                      role="row"
                      aria-selected={selectable ? isSelected : undefined}
                      className={`
                        contents
                        ${
                          selectable
                            ? 'cursor-pointer hover:bg-neutral-50 focus:bg-neutral-50'
                            : ''
                        }
                        ${isSelected ? 'bg-blue-50' : ''}
                        ${styling.rowClassName || ''}
                      `}
                    >
                      {renderDataSection(leftPinnedColumns, row, rowIndex, 0)}
                    </div>
                  );
                })}
              </div>
            )}

            {unpinnedColumns.length > 0 && (
              <div
                className="flex-1"
                style={{
                  display: 'grid',
                  gridTemplateColumns: unpinnedGrid,
                }}
                role="rowgroup"
                aria-label="Unpinned columns"
              >
                <div className="contents" role="row">
                  {renderColumnSection(
                    unpinnedColumns,
                    leftPinnedColumns.length
                  )}
                </div>

                {data?.results?.map((row, rowIndex) => {
                  const rowId = getRowId(row);
                  const isSelected = selectedRows.includes(rowId);

                  return (
                    <div
                      key={`unpinned-row-${rowIndex}`}
                      role="row"
                      aria-selected={selectable ? isSelected : undefined}
                      tabIndex={selectable ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (
                          selectable &&
                          (e.key === 'Enter' || e.key === ' ')
                        ) {
                          e.preventDefault();
                          handleRowClick(row, rowIndex);
                        }
                      }}
                      onClick={() => handleRowClick(row, rowIndex)}
                      className={`
                        contents
                        ${
                          selectable
                            ? 'cursor-pointer hover:bg-neutral-50 focus:bg-neutral-50'
                            : ''
                        }
                        ${isSelected ? 'bg-blue-50' : ''}
                        ${styling.rowClassName || ''}
                      `}
                    >
                      {renderDataSection(
                        unpinnedColumns,
                        row,
                        rowIndex,
                        leftPinnedColumns.length
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {rightPinnedColumns.length > 0 && (
              <div
                className="flex-shrink-0 sticky right-0 z-20 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.1)]"
                style={{
                  display: 'grid',
                  gridTemplateColumns: rightPinnedGrid,
                }}
                role="rowgroup"
                aria-label="Right pinned columns"
              >
                <div className="contents" role="row">
                  {renderColumnSection(
                    rightPinnedColumns,
                    leftPinnedColumns.length + unpinnedColumns.length
                  )}
                </div>

                {data?.results?.map((row, rowIndex) => {
                  const rowId = getRowId(row);
                  const isSelected = selectedRows.includes(rowId);

                  return (
                    <div
                      key={`right-pinned-row-${rowIndex}`}
                      role="row"
                      aria-selected={selectable ? isSelected : undefined}
                      className={`
                        contents
                        ${
                          selectable
                            ? 'cursor-pointer hover:bg-neutral-50 focus:bg-neutral-50'
                            : ''
                        }
                        ${isSelected ? 'bg-blue-50' : ''}
                        ${styling.rowClassName || ''}
                      `}
                    >
                      {renderDataSection(
                        rightPinnedColumns,
                        row,
                        rowIndex,
                        leftPinnedColumns.length + unpinnedColumns.length
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {showPagination && (
            <PaginationComponent
              count={data.count}
              sortValues={sortValues}
              setSortValues={handleSortValues}
            />
          )}

          {isLoading && loadingState.overlay !== false && (
            <div
              className={`absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-20 ${
                loadingState.className || ''
              }`}
              role="status"
              aria-live="polite"
            >
              <div className="text-neutral-500 font-medium">
                {loadingState.message || 'Loading...'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataTable;
